import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiTokensAPI, webhooksAPI, authAPI, integracionesConfigAPI } from '../services/api';
import './Integraciones.css';

const EVENTOS_WEBHOOK = [
  'cliente.creado', 'cliente.actualizado',
  'cita.creada', 'cita.actualizada', 'cita.eliminada',
  'oportunidad.creada', 'oportunidad.actualizada', 'oportunidad.eliminada',
  'factura.creada', 'factura.actualizada',
  'tarea.creada', 'tarea.completada',
];

function Integraciones() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('tokens');
  const [tokens, setTokens] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [accesoPermitido, setAccesoPermitido] = useState(null);

  // Verificar que el usuario tiene integraciones habilitadas
  useEffect(() => {
    authAPI.check()
      .then(res => {
        const u = res.data?.user;
        const permitido = u && (u.integraciones_habilitadas === true || u.integraciones_habilitadas === 1);
        setAccesoPermitido(permitido);
        if (!permitido && res.data?.authenticated) {
          navigate('/', { replace: true });
        }
      })
      .catch(() => setAccesoPermitido(false));
  }, [navigate]);

  const [nuevoTokenNombre, setNuevoTokenNombre] = useState('');
  const [tokenCreado, setTokenCreado] = useState(null);

  const [nuevoWebhook, setNuevoWebhook] = useState({ url: '', eventos: [], secret: '' });
  const [eventosDisponibles, setEventosDisponibles] = useState(EVENTOS_WEBHOOK);

  const [icalConfig, setIcalConfig] = useState(null);
  const [icalLoading, setIcalLoading] = useState(false);

  const loadTokens = async () => {
    try {
      const r = await apiTokensAPI.getAll();
      let data = r.data;
      // Aceptar tanto array puro como objetos envueltos o respuestas en texto
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          data = [];
        }
      }
      if (Array.isArray(data)) {
        setTokens(data);
      } else if (Array.isArray(data?.data)) {
        setTokens(data.data);
      } else {
        setTokens([]);
      }
    } catch (e) {
      setTokens([]);
      setError(e.response?.data?.error || 'Error al cargar tokens');
    }
  };

  const loadWebhooks = async () => {
    try {
      const r = await webhooksAPI.getAll();
      const data = r.data?.webhooks ?? r.data;
      setWebhooks(Array.isArray(data) ? data : []);
      if (Array.isArray(r.data?.eventos_disponibles)) {
        setEventosDisponibles(r.data.eventos_disponibles);
      }
    } catch (e) {
      setWebhooks([]);
      setError(e.response?.data?.error || 'Error al cargar webhooks');
    }
  };

  const loadIcalConfig = async () => {
    try {
      const r = await integracionesConfigAPI.get();
      setIcalConfig(r.data);
    } catch (e) {
      setIcalConfig(null);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([loadTokens(), loadWebhooks(), loadIcalConfig()]).finally(() => setLoading(false));
  }, []);

  const handleCrearToken = async (e) => {
    e.preventDefault();
    setError(null);
    setTokenCreado(null);
    const nombre = nuevoTokenNombre.trim();
    if (!nombre) {
      setError('Indica un nombre para el token (ej: Zapier, mi script).');
      return;
    }
    try {
      const r = await apiTokensAPI.create(nombre);
      let payload = r.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          // Si no es JSON válido, al menos mostramos el texto crudo como "token"
          payload = { token: payload };
        }
      }
      setTokenCreado(payload);
      setNuevoTokenNombre('');
      loadTokens();
      setSuccess('Token creado. Copia el valor ahora; no se volverá a mostrar.');
      setTimeout(() => { setSuccess(null); setTokenCreado(null); }, 15000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear token');
    }
  };

  const handleRevocarToken = async (id) => {
    if (!window.confirm('¿Revocar este token? Las integraciones que lo usen dejarán de funcionar.')) return;
    setError(null);
    try {
      await apiTokensAPI.delete(id);
      loadTokens();
      setSuccess('Token revocado.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al revocar');
    }
  };

  const toggleEvento = (ev) => {
    setNuevoWebhook((w) => ({
      ...w,
      eventos: w.eventos.includes(ev) ? w.eventos.filter((e) => e !== ev) : [...w.eventos, ev],
    }));
  };

  const handleCrearWebhook = async (e) => {
    e.preventDefault();
    setError(null);
    const url = nuevoWebhook.url.trim();
    if (!url) {
      setError('La URL del webhook es obligatoria.');
      return;
    }
    if (nuevoWebhook.eventos.length === 0) {
      setError('Selecciona al menos un evento.');
      return;
    }
    try {
      await webhooksAPI.create({
        url,
        eventos: nuevoWebhook.eventos,
        secret: nuevoWebhook.secret.trim() || undefined,
      });
      setNuevoWebhook({ url: '', eventos: [], secret: '' });
      loadWebhooks();
      setSuccess('Webhook creado.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear webhook');
    }
  };

  const handleEliminarWebhook = async (id) => {
    if (!window.confirm('¿Eliminar este webhook?')) return;
    setError(null);
    try {
      await webhooksAPI.delete(id);
      loadWebhooks();
      setSuccess('Webhook eliminado.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  if (accesoPermitido === null || accesoPermitido === false) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="integraciones-page">
      <div className="page-header">
        <h2>Integraciones</h2>
        <p className="subtitle">Tokens de API y webhooks para conectar otros sistemas con tu CRM.</p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="integraciones-tabs">
        <button type="button" className={tab === 'calendario' ? 'active' : ''} onClick={() => setTab('calendario')}>
          📅 Calendario
        </button>
        <button type="button" className={tab === 'tokens' ? 'active' : ''} onClick={() => setTab('tokens')}>
          Tokens de API
        </button>
        <button type="button" className={tab === 'webhooks' ? 'active' : ''} onClick={() => setTab('webhooks')}>
          Webhooks
        </button>
      </div>

      {loading ? (
        <p>Cargando...</p>
      ) : (
        <>
          {tab === 'calendario' && (
            <div className="integraciones-section">
              <h3>Sincronización de Calendario</h3>
              <p>Sincroniza tus citas con Google Calendar, Apple Calendar, Outlook y cualquier aplicación compatible con iCal.</p>

              <div style={{ marginTop: 20, padding: 20, background: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
                <h4 style={{ margin: '0 0 12px' }}>Feed iCal</h4>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: 16 }}>
                  Genera una URL de feed iCal que puedes añadir a cualquier aplicación de calendario. Las citas se sincronizarán automáticamente.
                </p>

                {icalConfig?.ical_url ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <input
                        type="text"
                        readOnly
                        value={icalConfig.ical_url}
                        style={{ flex: 1, padding: '8px 12px', border: '1px solid #ccc', borderRadius: 4, fontSize: '13px', fontFamily: 'monospace', background: '#fff' }}
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => { navigator.clipboard?.writeText(icalConfig.ical_url); setSuccess('URL copiada'); setTimeout(() => setSuccess(null), 2000); }}
                      >
                        Copiar
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                      <a
                        href={icalConfig.gcal_subscribe_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ background: '#4285F4', color: '#fff', border: 'none', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        Añadir a Google Calendar
                      </a>
                      <a
                        href={icalConfig.ical_url}
                        className="btn btn-secondary btn-sm"
                        style={{ textDecoration: 'none' }}
                      >
                        Descargar .ics
                      </a>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#dc3545', color: '#fff', border: 'none' }}
                        disabled={icalLoading}
                        onClick={async () => {
                          if (!window.confirm('¿Revocar el feed iCal? Los calendarios que lo usen dejarán de sincronizar.')) return;
                          setIcalLoading(true);
                          try {
                            const r = await integracionesConfigAPI.revokeIcalToken();
                            setIcalConfig(r.data);
                            setSuccess('Feed iCal revocado');
                            setTimeout(() => setSuccess(null), 3000);
                          } catch (e) { setError('Error al revocar'); }
                          finally { setIcalLoading(false); }
                        }}
                      >
                        Revocar feed
                      </button>
                    </div>
                    <p style={{ fontSize: '13px', color: '#888' }}>
                      <strong>Instrucciones:</strong> Copia la URL y pégala en tu aplicación de calendario como "suscripción" o "calendario por URL". 
                      En Google Calendar: Otros calendarios → Desde URL → pegar la URL.
                    </p>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary"
                    disabled={icalLoading}
                    onClick={async () => {
                      setIcalLoading(true);
                      try {
                        const r = await integracionesConfigAPI.generateIcalToken();
                        setIcalConfig(r.data);
                        setSuccess('Feed iCal generado');
                        setTimeout(() => setSuccess(null), 3000);
                      } catch (e) { setError('Error al generar feed'); }
                      finally { setIcalLoading(false); }
                    }}
                  >
                    Generar feed iCal
                  </button>
                )}
              </div>

              <div style={{ marginTop: 24, padding: 20, background: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
                <h4 style={{ margin: '0 0 12px' }}>Añadir citas a Google Calendar</h4>
                <p style={{ fontSize: '14px', color: '#666' }}>
                  También puedes añadir citas individuales a Google Calendar directamente desde la vista de citas. 
                  Cada cita tiene un botón "Añadir a Google Calendar" en la vista de lista.
                </p>
              </div>
            </div>
          )}

          {tab === 'tokens' && (
            <div className="integraciones-section">
              <h3>Tokens de API</h3>
              <p>Usa un token en lugar de sesión para llamar a la API desde otro sistema. Envía el header <code>Authorization: Bearer TU_TOKEN</code> o <code>X-Api-Key: TU_TOKEN</code>.</p>
              {tokenCreado && (
                <div className="alert alert-success token-created-box">
                  <strong>Token creado.</strong> Cópialo ahora; no se volverá a mostrar.
                  <div className="token-value" style={{ marginTop: 8, padding: 12, background: '#f0f0f0', borderRadius: 6, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {tokenCreado.token}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm token-copy-btn"
                    style={{ marginTop: 8 }}
                    onClick={() => navigator.clipboard?.writeText(tokenCreado.token)}
                  >
                    Copiar
                  </button>
                </div>
              )}
              <form onSubmit={handleCrearToken} className="form-inline" style={{ marginTop: 16, marginBottom: 24 }}>
                <input
                  type="text"
                  value={nuevoTokenNombre}
                  onChange={(e) => setNuevoTokenNombre(e.target.value)}
                  placeholder="Nombre (ej: Zapier, mi script)"
                  style={{ padding: 8, marginRight: 8, minWidth: 200 }}
                />
                <button type="submit" className="btn btn-primary">Crear token</button>
              </form>
              <table className="table">
                <thead>
                  <tr><th>Nombre</th><th>Prefijo</th><th>Último uso</th><th></th></tr>
                </thead>
                <tbody>
                  {tokens.map((t) => (
                    <tr key={t.id}>
                      <td>{t.nombre}</td>
                      <td><code>{t.prefijo_visible}…</code></td>
                      <td>{t.ultimo_uso ? new Date(t.ultimo_uso).toLocaleString('es-ES') : '—'}</td>
                      <td>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRevocarToken(t.id)}>Revocar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tokens.length === 0 && !tokenCreado && <p className="empty-msg">No hay tokens. Crea uno para integrar con otros sistemas.</p>}
            </div>
          )}

          {tab === 'webhooks' && (
            <div className="integraciones-section">
              <h3>Webhooks</h3>
              <p>Recibe notificaciones HTTP cuando ocurran eventos en tu CRM (cliente creado, cita actualizada, etc.).</p>
              <form onSubmit={handleCrearWebhook} className="webhook-form" style={{ marginTop: 16, marginBottom: 24 }}>
                <div className="form-group">
                  <label>URL *</label>
                  <input
                    type="url"
                    value={nuevoWebhook.url}
                    onChange={(e) => setNuevoWebhook((w) => ({ ...w, url: e.target.value }))}
                    placeholder="https://tu-servidor.com/webhook"
                    style={{ width: '100%', maxWidth: 400, padding: 8 }}
                  />
                </div>
                <div className="form-group">
                  <label>Eventos (mínimo uno)</label>
                  <div className="eventos-checkboxes" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {eventosDisponibles.map((ev) => (
                      <label key={ev} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={nuevoWebhook.eventos.includes(ev)} onChange={() => toggleEvento(ev)} />
                        <span>{ev}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Secreto (opcional, para firmar las peticiones)</label>
                  <input
                    type="text"
                    value={nuevoWebhook.secret}
                    onChange={(e) => setNuevoWebhook((w) => ({ ...w, secret: e.target.value }))}
                    placeholder="Dejar vacío si no usas firma"
                    style={{ width: '100%', maxWidth: 300, padding: 8 }}
                  />
                </div>
                <button type="submit" className="btn btn-primary">Añadir webhook</button>
              </form>
              <table className="table">
                <thead>
                  <tr><th>URL</th><th>Eventos</th><th></th></tr>
                </thead>
                <tbody>
                  {webhooks.map((w) => (
                    <tr key={w.id}>
                      <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={w.url}>{w.url}</td>
                      <td>{(w.eventos || []).join(', ')}</td>
                      <td>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => handleEliminarWebhook(w.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {webhooks.length === 0 && <p className="empty-msg">No hay webhooks. Añade una URL para recibir notificaciones.</p>}
            </div>
          )}
        </>
      )}
      <div className="integraciones-doc" style={{ marginTop: 32, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
        <h4>Documentación</h4>
        <p>
          Consulta la{' '}
          <a href="/docs/DOCUMENTACION_API.md" target="_blank" rel="noopener noreferrer">
            guía completa de la API
          </a>{' '}
          para la lista de endpoints, autenticación con token y formato de los webhooks.
        </p>
      </div>
    </div>
  );
}

export default Integraciones;
